import { useState, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit, Trash2, Image } from 'lucide-react';

const AdminHeroBanners = () => {
  const { API } = useGlobal();
  const { token } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    page: '',
    main_headline: '',
    subheadline: '',
    background_image: '',
    cta_text: '',
    cta_link: ''
  });

  const pages = [
    { value: 'Home', label: 'Homepage' },
    { value: 'Mobility', label: 'Mobility Imaging' },
    { value: 'Surveillance', label: 'Surveillance' },
    { value: 'Ultrasonic', label: 'Ultrasonic' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'About', label: 'About Us' },
    { value: 'Contact', label: 'Contact' },
  ];

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/hero-banners`);
      setBanners(response.data);
    } catch (error) {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, [API]);

  const resetForm = () => {
    setFormData({
      page: '',
      main_headline: '',
      subheadline: '',
      background_image: '',
      cta_text: '',
      cta_link: ''
    });
    setEditingBanner(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingBanner) {
        await axios.put(`${API}/hero-banners/${editingBanner.id}`, formData, { headers });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/hero-banners`, formData, { headers });
        toast.success('Banner created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      page: banner.page,
      main_headline: banner.main_headline,
      subheadline: banner.subheadline || '',
      background_image: banner.background_image || '',
      cta_text: banner.cta_text || '',
      cta_link: banner.cta_link || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hero-banners/${id}`, { headers });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  return (
    <div data-testid="admin-hero-banners">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">Hero Banners</h1>
          <p className="text-slate-600">Manage page hero sections</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#5b3589] hover:bg-[#4c1d95] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Banner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat']">
                {editingBanner ? 'Edit Hero Banner' : 'Add Hero Banner'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Page *</Label>
                <Select 
                  value={formData.page} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, page: v }))}
                  disabled={!!editingBanner}
                >
                  <SelectTrigger><SelectValue placeholder="Select page" /></SelectTrigger>
                  <SelectContent>
                    {pages.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Main Headline *</Label>
                <Input
                  value={formData.main_headline}
                  onChange={(e) => setFormData(prev => ({ ...prev, main_headline: e.target.value }))}
                  required
                  placeholder="Primary heading text"
                />
              </div>

              <div className="space-y-2">
                <Label>Subheadline</Label>
                <Textarea
                  value={formData.subheadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, subheadline: e.target.value }))}
                  rows={2}
                  placeholder="Supporting text"
                />
              </div>

              <div className="space-y-2">
                <Label>Background Image URL</Label>
                <Input
                  value={formData.background_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, background_image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CTA Button Text</Label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                    placeholder="Learn More"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CTA Link</Label>
                  <Input
                    value={formData.cta_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                    placeholder="/contact or https://..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#5b3589] hover:bg-[#4c1d95] text-white">
                  {editingBanner ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="bg-slate-100 animate-pulse h-32 rounded-sm" />)}
        </div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
          <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hero banners yet</h3>
          <p className="text-slate-500 mb-4">Add banners to customize page headers</p>
          <Button onClick={() => setIsDialogOpen(true)} className="bg-[#5b3589] hover:bg-[#4c1d95] text-white">
            <Plus className="w-4 h-4 mr-2" />Add Banner
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {banners.map(banner => (
            <div key={banner.id} className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="flex">
                <div className="w-48 h-32 bg-slate-100 shrink-0">
                  {banner.background_image ? (
                    <img src={banner.background_image} alt={banner.page} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Image className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[#5b3589] bg-purple-50 px-2 py-1 rounded">
                      {banner.page}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(banner)}>
                        <Edit className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(banner.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3 h-3 mr-1" />Delete
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{banner.main_headline}</h3>
                  <p className="text-slate-600 text-sm line-clamp-1">{banner.subheadline}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminHeroBanners;
